import {Component, Injectable, Input, OnChanges} from '@angular/core';
import {SearchHandlerService} from '../../search-handler.service';
import {AConst} from '../../../core/a-const.enum';
import {OverviewField} from '../../../core/definitions/object-view';
import {SearchObject} from '../../../core/definitions/search-object';
import {SearchResultSelectionsService} from '../../search-result-selections.service';
import {SearchStateService} from '../../search-state.service';
import {SearchViewListService} from '../../search-view-list.service';
import {SearchContainer} from '../../../core/definitions/search-container';
import {MetaTypes} from '../../../core/definitions/meta-types';
import {FlatTreeControl} from '@angular/cdk/tree';
import {HierarchicSearchService} from '../../hierarchic-search.service';
import {BehaviorSubject, merge, Observable} from 'rxjs';
import {CollectionViewer, SelectionChange} from '@angular/cdk/collections';
import {map} from 'rxjs/operators';
import {MatTreeFlatDataSource, MatTreeFlattener} from '@angular/material/tree';
import {RefData} from '../../../core/ref.service';
import {ResultViewService} from '../../result-view.service';

export class ListFlatNode {
  name: string;
  artifact_id: string;
  used: boolean;
  refData: RefData;
  icon: string;
  meta_type: string;
  is_leaf: boolean;

  constructor(public item: SearchObject,
              public level = 1,
              public expandable = false,
              public isLoading = false) {
    this.name = item.$$name;
    this.artifact_id = item.artifact_id;
    this.used = item.$$used;
    this.refData = item.$$refData;
    this.icon = item.$$icon;
    this.meta_type = item.meta_type;
    this.is_leaf = item.is_leaf;
  }
}

@Injectable()
export class ListDatabase {
  constructor(private hierarchicSearchService: HierarchicSearchService) {
  }
  initialData(searchObjects: SearchObject[]): ListFlatNode[] {
    return searchObjects.map(searchObject => new ListFlatNode(searchObject, 1, true));
  }

  async getChildren(searchContainer: SearchContainer, node: SearchObject): Promise<SearchObject[]> {
    const children = await this.hierarchicSearchService.getChildren(searchContainer, node);
    node.$$hasChildren = !!children.length;
    return children;
  }

  isExpandable(node: SearchObject) {
    let res = ['folder', 'place'].indexOf(node.object_type) !== -1;
    if (node.$$hasChildren !== undefined) {
      res = node.$$hasChildren;
    }
    return res;
  }
}

@Injectable()
export class ListDataSource extends MatTreeFlatDataSource<any, any> {
  dataChange = new BehaviorSubject<ListFlatNode[]>([]);

  get data(): ListFlatNode[] {
    return this.dataChange.value;
  }

  set data(value: ListFlatNode[]) {
    this.treeControl.dataNodes = value;
    this.dataChange.next(value);
  }

  constructor(private treeControl: FlatTreeControl<ListFlatNode>,
              private treeFlattener: MatTreeFlattener<any, any>,
              private database: ListDatabase,
              private searchContainer: SearchContainer) {
    super(treeControl, treeFlattener);
  }

  connect(collectionViewer: CollectionViewer): Observable<ListFlatNode[]> {
    this.treeControl.expansionModel.changed.subscribe(change => {
      if (change.added || change.removed) {
        this.handleTreeControl(change);
      }
    });
    return merge(collectionViewer.viewChange, this.dataChange).pipe(map(() => this.data));
  }

  handleTreeControl(change: SelectionChange<ListFlatNode>) {
    if (change.added) {
      change.added.forEach(node => this.toggleNode(node, true));
    }
    if (change.removed) {
      change.removed.slice().reverse().forEach(node => this.toggleNode(node, false));
    }
  }

  async toggleNode(node: ListFlatNode, expand: boolean): Promise<void> {
    node.isLoading = true;
    const children = await this.database.getChildren(this.searchContainer, node.item);
    const index = this.data.indexOf(node);
    if (!children.length || index < 0) {
      return;
    }

    if (expand) {
      const nodes = children.map(searchObject => new ListFlatNode(searchObject, node.level + 1, this.database.isExpandable(searchObject)));
      this.data.splice(index + 1, 0, ...nodes);
    } else {
      this.data.splice(index + 1, children.length);
    }
    this.dataChange.next(this.data);
    node.isLoading = false;
  }
}

@Component({
  selector: 'app-search-result-view-list',
  templateUrl: './search-result-view-list.component.html',
  styleUrls: ['./search-result-view-list.component.scss'],
  providers: [ListDatabase]
})
export class SearchResultViewListComponent implements OnChanges {

  @Input() searchContainer: SearchContainer;

  MetaTypes = MetaTypes;
  AConst = AConst;
  objectFields;
  displayedCol: string[] = [];
  treeControl: FlatTreeControl<ListFlatNode>;
  dataSource: ListDataSource;

  private subTitleCounter = {count: 0};
  private transformer = (node: SearchObject, level: number) => {
    return {
      expandable: node.$$hasChildren === undefined || node.$$hasChildren,
      level: level,
      item: node,
      name: node.$$name,
      artifact_id: node.artifact_id,
      used: node.$$used,
      refData: node.$$refData,
      icon: node.$$icon,
      meta_type: node.meta_type,
      is_leaf: node.is_leaf
    };
  }

  constructor(
    private database: ListDatabase,
    private searchViewListService: SearchViewListService,
    private searchHandler: SearchHandlerService,
    private searchResultSelectionsService: SearchResultSelectionsService,
    private searchStateService: SearchStateService,
    private resultViewService: ResultViewService) {
  }

  getLevel = (node: ListFlatNode) => node.level;

  isExpandable = (node: ListFlatNode) => {
    if (!!node.is_leaf) {
      return false;
    } else {
      return this.database.isExpandable(node.item);
    }
  }

  getChildren = (node: ListFlatNode) => {
    return this.getChildren(node);
  }

  ngOnChanges(): void {
    this.init().then();
  }

  onOperationPerformed(event) {
    console.log('Event: ' + event);
  }

  setOrder(fieldInfo) {
    if (fieldInfo.field_name === 'artifact_name' || fieldInfo.isSortable) {
      this.searchHandler.setOrder(this.searchContainer, fieldInfo.field_name, fieldInfo);
    }
  }

  select(element: ListFlatNode, event) {
    this.searchResultSelectionsService.selectSearchResultItem(element.item, this.searchContainer, event.shiftKey);
    this.searchStateService.setState(this.searchContainer).catch();
  }

  setTitles() {
    for (const field of this.searchContainer.currentPathView.search_view.overview_fields) {
      field.$$fieldTitle = this.searchViewListService.getTitle(field, this.subTitleCounter);
    }
  }

  getObjectFields(element: ListFlatNode): Array<OverviewField> {
    const art = element.item;
    const objectId = art.artifact_id;
    let res = this.objectFields[objectId];
    if (!res) {
      /* In some cases, the search result is changed after objectFields is set, causing some issues*/
      this.objectFields[art.artifact_id] = art.overview;
      res = this.objectFields[objectId];
    }
    return res;
  }

  setSearchItemIndex(searchItemIndex) {
    if (this.searchContainer && searchItemIndex !== undefined) {
      this.searchHandler.setSearchItemIndex(this.searchContainer, searchItemIndex);
    }
  }

  private async init() {
    this.treeControl = new FlatTreeControl<ListFlatNode, ListFlatNode>(this.getLevel, this.isExpandable);
    const treeFlattener = new MatTreeFlattener(
      this.transformer, node => node.level, node => node.expandable, () => []);
    this.dataSource = new ListDataSource(this.treeControl, treeFlattener, this.database, this.searchContainer);
    await this.checkSetSearchResultItemProps();
    this.dataSource.data = this.database.initialData(this.searchContainer.searchResult.artifacts);


    this.objectFields = {};
    this.searchViewListService.initObjectFields(this.searchContainer, this.objectFields);
    this.searchViewListService.setDisplayedColumns(this.searchContainer, this.displayedCol);
    this.setTitles();
    this.displayedCol.push('setting');

    this.searchContainer.newScrollItemsCallback = (newItems) => {
      this.dataSource.data = this.dataSource.data.concat(newItems.map(item => new ListFlatNode(item)));
    };
  }

  private async checkSetSearchResultItemProps() {
    const searchObjects = this.searchContainer.searchResult.artifacts;
    if (searchObjects.length && !searchObjects[0].$$refData) {
      // This condition should actually never happen. But somehow the testers have reported errors probably due to
      // missing $$refData so that's the reason for this function.
      await this.resultViewService.setSearchResultItemProps(this.searchContainer, searchObjects);
    }
  }
}
